/**
 * Browser-side image resizing, so an upload carries the size it will be shown at rather than
 * the 6 MB photo it came from.
 *
 * Each call makes its own canvas and releases it before returning. Nothing outlives the call,
 * so there is nothing for a component to clean up on unmount. A shared canvas would save
 * nothing measurable, and two resizes in flight at once would draw over each other.
 */

export interface ResizeOptions {
  width: number;
  height: number;
  /** Output type, as `canvas.toDataURL` takes it. */
  type?: 'image/jpeg' | 'image/png' | 'image/webp';
  /** 0–1, for the lossy types. */
  quality?: number;
  /** Painted under the image: JPEG has no alpha, and a transparent PNG would otherwise come out black. */
  background?: string;
}

/** The source rectangle that fills `width × height` without distorting: centred, cropping the overflow. */
export function coverCrop(sourceW: number, sourceH: number, width: number, height: number) {
  const scale = Math.max(width / sourceW, height / sourceH);
  const w = width / scale;
  const h = height / scale;
  return { x: (sourceW - w) / 2, y: (sourceH - h) / 2, w, h };
}

/**
 * The image in `file`, cropped to fill `width × height` and re-encoded, as a data URL.
 * null when the browser can't decode it.
 */
export async function resizeImage(
  file: Blob,
  { width, height, type = 'image/jpeg', quality = 0.82, background = '#fff' }: ResizeOptions,
): Promise<string | null> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }
  const canvas = document.createElement('canvas');
  try {
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);
    const crop = coverCrop(bitmap.width, bitmap.height, width, height);
    ctx.drawImage(bitmap, crop.x, crop.y, crop.w, crop.h, 0, 0, width, height);
    return canvas.toDataURL(type, quality);
  } finally {
    bitmap.close();
    // Drops the backing store now, not at GC: Safari caps the total canvas memory in use.
    canvas.width = 0;
    canvas.height = 0;
  }
}
